import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail, Lock, User, Phone,
  ExternalLink, MapPin, CheckCircle, Shield, Brain, Sparkles,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { LoginForm, RegisterForm } from '../types';
import { AU_UNIVERSITIES } from '../constants/universities';
import { AUSTRALIAN_SUBURBS } from '../constants/australianSuburbs';
import { useToast } from '../contexts/ToastContext';
import apiService from '../services/api';
import GoogleIcon from '../components/ui/GoogleIcon';
import LinkedInIcon from '../components/ui/LinkedInIcon';
import { extractErrorMessage } from '../utils/errorUtils';

/* ── Left-panel feature list ───────────────────────────────── */
const LEFT_FEATURES = [
  { icon: <Shield className="h-4 w-4" />,  text: 'Visa-aware job matching' },
  { icon: <Brain  className="h-4 w-4" />,  text: 'AI-powered recommendations' },
  { icon: <CheckCircle className="h-4 w-4" />, text: 'Verified employer network' },
  { icon: <Sparkles className="h-4 w-4" />, text: 'Early adopter perks' },
];

const AuthPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const { login, loginWithOTP, register } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  /* MFA state */
  const [mfaRequired,    setMfaRequired]    = useState(false);
  const [mfaToken,       setMfaToken]       = useState('');
  const [otpCode,        setOtpCode]        = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);

  /* Set initial tab from URL */
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'register' || tab === 'signup') {
      setActiveTab('register');
      setRegisterForm(p => ({ ...p, role: 'student' }));
    } else if (tab === 'login') {
      setActiveTab('login');
    }
  }, [searchParams]);

  const [loginForm, setLoginForm] = useState<LoginForm>({ email: '', password: '' });

  const [registerForm, setRegisterForm] = useState<RegisterForm>({
    email: '', password: '', full_name: '', contact_number: '',
    role: 'student', university: '', degree: '', graduation_year: undefined, visa_status: '',
  });

  const [firstName,   setFirstName]   = useState('');
  const [lastName,    setLastName]     = useState('');
  const [dob,         setDob]          = useState('');
  const [citySuburb,  setCitySuburb]   = useState('');
  const [citySuggestions,    setCitySuggestions]    = useState<string[]>([]);
  const [showCitySuggestions,setShowCitySuggestions]= useState(false);

  const visaOptions = useMemo(() => [
    'Student Visa (subclass 500)',
    'Temporary Graduate (subclass 485)',
    'Skilled Independent (subclass 189)',
    'Skilled Nominated (subclass 190)',
    'Skilled Work Regional (subclass 491)',
    'Employer Sponsored TSS (subclass 482)',
    'Employer Nomination (subclass 186)',
    'Working Holiday (subclass 417/462)',
    'Other/Not Sure',
  ], []);

  const gradYears = useMemo(() => {
    const cur = new Date().getFullYear();
    const years: number[] = [];
    for (let y = cur - 10; y <= cur + 6; y++) years.push(y);
    return years;
  }, []);

  const handleCityChange = (value: string) => {
    setCitySuburb(value);
    if (value.length > 1) {
      setCitySuggestions(AUSTRALIAN_SUBURBS.filter(s => s.toLowerCase().includes(value.toLowerCase())).slice(0, 10));
      setShowCitySuggestions(true);
    } else {
      setCitySuggestions([]);
      setShowCitySuggestions(false);
    }
  };

  /* OAuth callback handling */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('oauth') === 'success' && params.get('access_token')) {
      const access  = params.get('access_token')!;
      const refresh = params.get('refresh_token') || '';
      localStorage.setItem('token', access);
      if (refresh) localStorage.setItem('refresh_token', refresh);
      window.history.replaceState({}, document.title, window.location.pathname);
      (async () => {
        try {
          const me = await apiService.getCurrentUser();
          localStorage.setItem('user', JSON.stringify(me));
          const provider = params.get('provider');
          toast(`Signed in with ${provider === 'linkedin' ? 'LinkedIn' : 'Google'}!`, 'success');
          setTimeout(() => {
            window.location.href = me.role === 'employer' ? '/employer/dashboard' : '/profile';
          }, 500);
        } catch {
          setError('Failed to complete sign-in');
        }
      })();
    } else if (params.get('oauth') === 'error') {
      const reason = params.get('reason');
      window.history.replaceState({}, document.title, window.location.pathname);
      setActiveTab('login');
      setError(
        reason === 'waitlist_required'
          ? "This account isn't on our approved Beta Waitlist yet. Join the waitlist to get access."
          : 'Failed to complete sign-in'
      );
    }
  }, [navigate, toast]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const result = await login(loginForm);
      if (result.mfa_required) {
        setMfaToken(result.mfa_token); setMfaRequired(true); setOtpCode('');
        return;
      }
      toast('Welcome back!', 'success');
      const cu = apiService.getCurrentUserFromStorage();
      navigate(cu?.role === 'employer' ? '/employer/dashboard' : '/profile');
    } catch (err: any) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      await loginWithOTP(mfaToken, otpCode, rememberDevice);
      toast('Successfully logged in', 'success');
      const cu = apiService.getCurrentUserFromStorage();
      navigate(cu?.role === 'employer' ? '/employer/dashboard' : '/profile');
    } catch (err: any) {
      setError(extractErrorMessage(err));
      if (err?.response?.status === 429) { setMfaRequired(false); setMfaToken(''); }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const full_name = `${firstName} ${lastName}`.trim();
      const payload: RegisterForm = { ...registerForm, full_name, city_suburb: citySuburb, date_of_birth: dob ? new Date(dob).toISOString() : undefined };
      if (payload.company_website && !/^https?:\/\//i.test(payload.company_website)) {
        payload.company_website = `https://${payload.company_website}`;
      }
      await register(payload);
      toast('Account created! Please sign in and verify your identity.', 'success', 5000);
      setActiveTab('login');
      setLoginForm({ email: payload.email, password: '' });
    } catch (err: any) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn   = (role?: 'student' | 'employer') => { window.location.href = role ? `${apiService.getGoogleLoginUrl()}?role=${role}` : apiService.getGoogleLoginUrl(); };
  const handleLinkedInSignIn = (role?: 'student' | 'employer') => { window.location.href = role ? `${apiService.getLinkedInLoginUrl()}?role=${role}` : apiService.getLinkedInLoginUrl(); };

  /* ── Tab animation ── */
  const tabVariants = {
    enter:  { opacity: 0, x: activeTab === 'login' ? -12 : 12 },
    center: { opacity: 1, x: 0 },
    exit:   { opacity: 0, x: activeTab === 'login' ?  12 : -12 },
  };

  return (
    <div className="min-h-screen flex">

      {/* ── Left panel (desktop only) ── */}
      <div className="hidden lg:flex lg:w-[42%] xl:w-[45%] flex-col justify-between bg-gradient-to-br from-primary-700 via-primary-800 to-secondary-800 p-12 relative overflow-hidden flex-shrink-0">
        {/* Orbs */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-teal-400 rounded-full opacity-10 -translate-y-1/3 translate-x-1/3 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-secondary-400 rounded-full opacity-10 translate-y-1/3 -translate-x-1/3 blur-3xl" />

        {/* Tagline */}
        <div className="relative z-10">
          <h2 className="text-3xl xl:text-4xl font-extrabold text-white leading-tight mb-6 tracking-tight">
            Your visa.<br />Your work rights.<br />
            <span className="text-teal-300">Your opportunity.</span>
          </h2>
          <ul className="space-y-3">
            {LEFT_FEATURES.map(({ icon, text }) => (
              <li key={text} className="flex items-center gap-3 text-white/80 text-sm">
                <span className="w-7 h-7 rounded-lg bg-white/15 flex items-center justify-center text-teal-300 flex-shrink-0">
                  {icon}
                </span>
                {text}
              </li>
            ))}
          </ul>
        </div>

        {/* Bottom quote */}
        <div className="relative z-10">
          <blockquote className="border-l-2 border-teal-400/60 pl-4">
            <p className="text-white/70 text-sm italic leading-relaxed">
              "Linzara directly addresses the real challenge of finding meaningful, visa-compliant work."
            </p>
            <cite className="text-white/50 text-xs mt-2 block not-italic">— Prof. Alex Zelinsky AO, VC, UoN</cite>
          </blockquote>
        </div>
      </div>

      {/* ── Right panel ── */}
      <div className="flex-1 flex flex-col min-h-screen bg-slate-100">

        <div className="flex-1 flex flex-col items-center justify-center px-5 sm:px-8 py-10">
          <div className="w-full max-w-[420px]">

            {/* Heading */}
            <div className="mb-5 text-center">
              <h1 className="text-2xl font-extrabold text-slate-900">
                {activeTab === 'login' ? 'Welcome back' : 'Create your account'}
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                {activeTab === 'login' ? 'Sign in to continue to Linzara.' : 'Join thousands of international job seekers.'}
              </p>
            </div>

            {/* Card */}
            <div className="bg-white rounded-2xl border border-slate-200 p-7" style={{boxShadow:'0 1px 3px rgba(0,0,0,0.10),0 4px 16px rgba(0,0,0,0.14)'}}>

            {/* Error */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4 text-sm flex items-start gap-2"
                >
                  <span className="flex-shrink-0">⚠️</span>
                  <span>{extractErrorMessage(error)}</span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab + (mfaRequired ? '-mfa' : '')}
                variants={tabVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >

                {/* ── Login ── */}
                {activeTab === 'login' && !mfaRequired && (
                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={() => handleLinkedInSignIn()}
                      className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-[#0A66C2] text-white rounded-xl text-sm font-semibold hover:bg-[#0958a8] transition-all duration-200 shadow-sm"
                    >
                      <LinkedInIcon className="h-4 w-4" />
                      Continue with LinkedIn
                    </button>
                    <button
                      type="button"
                      onClick={() => handleGoogleSignIn()}
                      className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl text-sm font-semibold hover:bg-slate-50 hover:border-slate-300 transition-all duration-200 shadow-sm"
                    >
                      <GoogleIcon className="h-4 w-4" />
                      Continue with Google
                    </button>

                    <div className="relative flex items-center gap-3 py-1">
                      <div className="flex-1 h-px bg-slate-200" />
                      <span className="text-xs text-slate-400 font-medium">or</span>
                      <div className="flex-1 h-px bg-slate-200" />
                    </div>

                    <form onSubmit={handleLogin} className="space-y-3">
                      <Input
                        label="Email"
                        type="email"
                        value={loginForm.email}
                        onChange={e => setLoginForm({ ...loginForm, email: e.target.value })}
                        icon={<Mail className="h-4 w-4" />}
                        required
                      />
                      <Input
                        label="Password"
                        type="password"
                        value={loginForm.password}
                        onChange={e => setLoginForm({ ...loginForm, password: e.target.value })}
                        icon={<Lock className="h-4 w-4" />}
                        required
                      />
                      <Button type="submit" loading={loading} className="w-full">Sign in with Email</Button>
                    </form>
                  </div>
                )}

                {/* ── OTP step ── */}
                {activeTab === 'login' && mfaRequired && (
                  <div className="space-y-5">
                    <div className="text-center">
                      <div className="w-12 h-12 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Mail className="h-6 w-6 text-primary-600" />
                      </div>
                      <h2 className="text-base font-bold text-slate-900">Check your email</h2>
                      <p className="text-sm text-slate-500 mt-1">
                        We sent a 6-digit code to{' '}
                        <span className="font-semibold text-slate-700">{loginForm.email}</span>.
                        Expires in 10 minutes.
                      </p>
                    </div>
                    <form onSubmit={handleVerifyOTP} className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Verification code</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          maxLength={6}
                          value={otpCode}
                          onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="000000"
                          className="w-full text-center text-2xl font-mono tracking-[0.4em] input-field py-4"
                          required
                        />
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-600">
                        <input
                          type="checkbox"
                          checked={rememberDevice}
                          onChange={e => setRememberDevice(e.target.checked)}
                          className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                        />
                        Remember this device for 2 weeks
                      </label>
                      <Button type="submit" loading={loading} className="w-full">Verify</Button>
                    </form>
                    <button
                      type="button"
                      onClick={() => { setMfaRequired(false); setMfaToken(''); setOtpCode(''); setError(null); }}
                      className="w-full text-sm text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      ← Back to sign in
                    </button>
                  </div>
                )}

                {/* ── Register ── */}
                {activeTab === 'register' && (
                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={() => handleLinkedInSignIn('student')}
                      className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-[#0A66C2] text-white rounded-xl text-sm font-semibold hover:bg-[#0958a8] transition-all duration-200 shadow-sm"
                    >
                      <LinkedInIcon className="h-4 w-4" />
                      Sign up with LinkedIn
                    </button>
                    <button
                      type="button"
                      onClick={() => handleGoogleSignIn('student')}
                      className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl text-sm font-semibold hover:bg-slate-50 hover:border-slate-300 transition-all duration-200 shadow-sm"
                    >
                      <GoogleIcon className="h-4 w-4" />
                      Sign up with Google
                    </button>

                    <div className="relative flex items-center gap-3 py-1">
                      <div className="flex-1 h-px bg-slate-200" />
                      <span className="text-xs text-slate-400 font-medium">or</span>
                      <div className="flex-1 h-px bg-slate-200" />
                    </div>

                    <form onSubmit={handleRegister} className="space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          label="First Name"
                          value={firstName}
                          onChange={e => { setFirstName(e.target.value); setRegisterForm(p => ({ ...p, full_name: `${e.target.value} ${lastName}`.trim() })); }}
                          icon={<User className="h-4 w-4" />}
                          required
                        />
                        <Input
                          label="Last Name"
                          value={lastName}
                          onChange={e => { setLastName(e.target.value); setRegisterForm(p => ({ ...p, full_name: `${firstName} ${e.target.value}`.trim() })); }}
                          required
                        />
                      </div>
                      <Input label="Email" type="email" value={registerForm.email} onChange={e => setRegisterForm({ ...registerForm, email: e.target.value })} icon={<Mail className="h-4 w-4" />} required />
                      <Input label="Contact Number" type="tel" placeholder="4XX XXX XXX" value={registerForm.contact_number || ''} onChange={e => { const v = e.target.value.replace(/^\+61\s*/, '').replace(/[^\d\s]/g, ''); setRegisterForm({ ...registerForm, contact_number: v ? `+61 ${v}` : '' }); }} icon={<Phone className="h-4 w-4" />} helperText="+61 added automatically" />
                      <Input label="Password" type="password" value={registerForm.password} onChange={e => setRegisterForm({ ...registerForm, password: e.target.value })} icon={<Lock className="h-4 w-4" />} helperText="Minimum 8 characters" required />

                      {registerForm.role === 'student' && (
                        <div className="space-y-3">
                          <Input label="Date of Birth" type="date" value={dob} onChange={e => setDob(e.target.value)} required />
                          <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Educational Institution <span className="font-normal text-slate-400">(Optional)</span></label>
                            <div className="relative">
                              <select value={registerForm.university || ''} onChange={e => setRegisterForm({ ...registerForm, university: e.target.value })} className="input-field">
                                <option value="">Select institution (if applicable)</option>
                                {AU_UNIVERSITIES.map(u => <option key={u} value={u}>{u}</option>)}
                              </select>
                            </div>
                          </div>
                          <div className="relative">
                            <label className="block text-sm font-semibold text-slate-700 mb-1.5">City / Suburb</label>
                            <div className="relative">
                              <input type="text" value={citySuburb} onChange={e => handleCityChange(e.target.value)} onBlur={() => setTimeout(() => setShowCitySuggestions(false), 150)} onFocus={() => citySuburb.length > 1 && setShowCitySuggestions(true)} placeholder="Start typing…" className="input-field" required />
                              {showCitySuggestions && citySuggestions.length > 0 && (
                                <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-52 overflow-y-auto">
                                  {citySuggestions.map((s, i) => <div key={i} className="px-4 py-2.5 hover:bg-slate-50 cursor-pointer text-sm" onMouseDown={() => { setCitySuburb(s); setShowCitySuggestions(false); }}>{s}</div>)}
                                </div>
                              )}
                            </div>
                          </div>
                          <Input label="Education / Qualification" placeholder="e.g. Bachelor of Computer Science" value={registerForm.degree || ''} onChange={e => setRegisterForm({ ...registerForm, degree: e.target.value })} required />
                          <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Graduation Year <span className="font-normal text-slate-400">(Optional)</span></label>
                            <div className="relative">
                              <select value={registerForm.graduation_year || ''} onChange={e => setRegisterForm({ ...registerForm, graduation_year: e.target.value ? parseInt(e.target.value) : undefined })} className="input-field">
                                <option value="">Select year</option>
                                {gradYears.map(y => <option key={y} value={y}>{y}</option>)}
                              </select>
                            </div>
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1.5">
                              <label className="block text-sm font-semibold text-slate-700">Visa Type</label>
                              <a href="https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing" target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:text-primary-600"><ExternalLink className="h-3.5 w-3.5" /></a>
                            </div>
                            <select value={registerForm.visa_status || ''} onChange={e => setRegisterForm({ ...registerForm, visa_status: e.target.value })} className="input-field">
                              <option value="">Select visa type</option>
                              {visaOptions.map(v => <option key={v} value={v}>{v}</option>)}
                            </select>
                          </div>
                        </div>
                      )}
                      <Button type="submit" loading={loading} className="w-full">Create Account</Button>
                    </form>
                  </div>
                )}

              </motion.div>
            </AnimatePresence>

            </div>{/* /card */}

            {/* Footer links */}
            <p className="text-center text-xs text-slate-400 mt-5">
              By continuing, you agree to our{' '}
              <Link to="/terms"   className="text-primary-600 hover:underline">Terms</Link> and{' '}
              <Link to="/privacy" className="text-primary-600 hover:underline">Privacy Policy</Link>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
